import React, { useEffect, useRef, useState } from 'react';

import { circlePolyline } from '../../util/maps';
import { loadMapboxGl } from '../../util/loadMapbox';

import css from './AirbnbStyleMap.module.css';

const MAPBOX_STYLE = 'mapbox://styles/mapbox/light-v11';
const FUZZY_CIRCLE_OPACITY = 0.18;
const ROUTE_ANIMATION_MS = 1400;

// Start loading the map library (and creating the map) this far before the map scrolls into
// view, so that it is usually ready when the visitor gets there.
const VIEWPORT_PRELOAD_MARGIN = '600px';

const buildMarkerEl = () => {
  const el = document.createElement('div');
  el.className = css.marker;
  const dot = document.createElement('div');
  dot.className = css.markerDot;
  el.appendChild(dot);
  return el;
};

const buildDestinationMarkerEl = () => {
  const el = document.createElement('div');
  el.className = css.destMarker;
  const pulse = document.createElement('div');
  pulse.className = css.destMarkerPulse;
  const core = document.createElement('div');
  core.className = css.destMarkerCore;
  el.appendChild(pulse);
  el.appendChild(core);
  return el;
};

const ROUTE_SOURCE = 'route-source';
const ROUTE_CASING_LAYER = 'route-casing';
const ROUTE_LINE_LAYER = 'route-line';

const easeOutCubic = t => 1 - Math.pow(1 - t, 3);

/**
 * Beautiful Airbnb-style Mapbox map with optional Uber-style animated
 * walking route + destination pulse for the nearest transit stop.
 *
 * Props:
 *  - center / obfuscatedCenter / mapsConfig / accessToken / zoom
 *  - route: { line: GeoJSON Feature<LineString> } | null
 *  - destination: { lng, lat } | null   marker for the route end
 */
const AirbnbStyleMap = props => {
  const { center, obfuscatedCenter, mapsConfig, accessToken, zoom = 14 } = props;
  const { route, destination } = props;

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const routeAnimationRef = useRef(null);
  // The map sits far below the fold on the listing page. Mapbox GL JS (~1 MB parsed) and the map
  // itself are only loaded once the container approaches the viewport.
  const [isNearViewport, setIsNearViewport] = useState(false);
  // Bumped once the map exists, so the route effect re-runs for a route that arrived earlier.
  const [mapReadyTick, setMapReadyTick] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof window.IntersectionObserver !== 'function') {
      setIsNearViewport(true);
      return undefined;
    }
    const observer = new window.IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: `${VIEWPORT_PRELOAD_MARGIN} 0px` }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const usesFuzzy = !!mapsConfig?.fuzzy?.enabled;
  const point = usesFuzzy ? obfuscatedCenter : center;
  const lng = point?.lng;
  const lat = point?.lat;

  // Initialize the map once.
  useEffect(() => {
    let cancelled = false;
    if (!isNearViewport || !accessToken || lng == null || lat == null) return undefined;

    (async () => {
      let mapboxgl = null;
      try {
        mapboxgl = await loadMapboxGl();
      } catch (e) {
        // Network error or content blocker: the empty map container stays.
      }
      if (cancelled || !mapboxgl || !containerRef.current) return;

      mapboxgl.accessToken = accessToken;

      // Clear any leftover mapbox DOM from a previous (HMR) instance —
      // otherwise mapbox-gl logs "container element should be empty" and
      // the new map renders an empty canvas with no tiles.
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }

      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: MAPBOX_STYLE,
        center: [lng, lat],
        zoom,
        scrollZoom: false,
        attributionControl: false,
        cooperativeGestures: true,
      });
      mapRef.current = map;
      setMapReadyTick(tick => tick + 1);

      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

      const onMapReady = () => {
        if (cancelled) return;
        if (usesFuzzy) {
          const path = circlePolyline(point, mapsConfig.fuzzy.offset).map(([a, b]) => [b, a]);
          if (!map.getSource('listing-fuzzy')) {
            map.addSource('listing-fuzzy', {
              type: 'geojson',
              data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [path] } },
            });
          }
          if (!map.getLayer('listing-fuzzy-fill')) {
            map.addLayer({
              id: 'listing-fuzzy-fill',
              type: 'fill',
              source: 'listing-fuzzy',
              paint: {
                'fill-color': mapsConfig.fuzzy.circleColor || '#222222',
                'fill-opacity': FUZZY_CIRCLE_OPACITY,
              },
            });
          }
          if (!map.getLayer('listing-fuzzy-outline')) {
            map.addLayer({
              id: 'listing-fuzzy-outline',
              type: 'line',
              source: 'listing-fuzzy',
              paint: {
                'line-color': mapsConfig.fuzzy.circleColor || '#222222',
                'line-width': 1.5,
                'line-opacity': 0.4,
              },
            });
          }
        } else {
          const el = buildMarkerEl();
          markerRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
            .setLngLat([lng, lat])
            .addTo(map);
        }

        // Pre-create the route source/layers so the route effect can just
        // call setData() instead of adding layers post-hoc (avoids ordering
        // glitches when the route arrives before the map is ready).
        //
        // Guard each add: `onMapReady` can fire twice in dev (React StrictMode
        // double-invoke, HMR re-mount of an already-loaded map, or Mapbox firing
        // `load` after we manually called us via `map.loaded()`). Without the
        // guards we crash with "There is already a source with ID …".
        if (!map.getSource(ROUTE_SOURCE)) {
          map.addSource(ROUTE_SOURCE, {
            type: 'geojson',
            data: {
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: [] },
              properties: {},
            },
          });
        }
        if (!map.getLayer(ROUTE_CASING_LAYER)) {
          map.addLayer({
            id: ROUTE_CASING_LAYER,
            type: 'line',
            source: ROUTE_SOURCE,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.95 },
          });
        }
        if (!map.getLayer(ROUTE_LINE_LAYER)) {
          map.addLayer({
            id: ROUTE_LINE_LAYER,
            type: 'line',
            source: ROUTE_SOURCE,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': '#222222', 'line-width': 4, 'line-opacity': 0.95 },
          });
        }

        map.resize();
        map.setCenter([lng, lat]);
      };
      // map.on('load') only fires for listeners attached BEFORE the load
      // completes. If the style/tiles already loaded (HMR re-mount, very
      // fast network), call our handler directly.
      if (map.loaded()) onMapReady();
      else map.once('load', onMapReady);

      const onResize = () => {
        if (mapRef.current) mapRef.current.resize();
      };
      window.addEventListener('resize', onResize);
      map._onWindowResize = onResize;
    })();

    return () => {
      cancelled = true;
      if (routeAnimationRef.current) cancelAnimationFrame(routeAnimationRef.current);
      const map = mapRef.current;
      if (map) {
        if (map._onWindowResize) window.removeEventListener('resize', map._onWindowResize);
        try {
          map.remove();
        } catch (e) {
          /* mapbox-gl can throw during HMR */
        }
        mapRef.current = null;
        markerRef.current = null;
        destMarkerRef.current = null;
      }
    };
  }, [isNearViewport, accessToken, lng, lat]);

  // Route + destination effect — animates the line draw and fits the map
  // bounds to include both the listing and the destination.
  useEffect(() => {
    let cancelled = false;

    const apply = () => {
      const map = mapRef.current;
      if (cancelled || !map) return;

      // Clear any prior destination marker on every change.
      if (destMarkerRef.current) {
        try {
          destMarkerRef.current.remove();
        } catch (e) {}
        destMarkerRef.current = null;
      }
      // Also sweep any orphan destination-marker DOM nodes that may have
      // been left behind by HMR / Strict-Mode double-invocations of the
      // route effect (mapbox-gl's .remove() can miss them if the marker
      // instance reference was lost).
      const container = map.getContainer?.();
      if (container) {
        container.querySelectorAll(`[class*="${css.destMarker}"]`).forEach(n => n.remove());
      }
      if (routeAnimationRef.current) cancelAnimationFrame(routeAnimationRef.current);

      const coords = route?.line?.geometry?.coordinates;

      // Ensure the route source + layers exist (they may not if map.on('load')
      // hasn't fired yet — we create them on-demand to avoid the init race).
      let source = map.getSource(ROUTE_SOURCE);
      if (!source) {
        const safeAdd = () => {
          if (cancelled || !mapRef.current) return;
          try {
            if (!map.getSource(ROUTE_SOURCE)) {
              map.addSource(ROUTE_SOURCE, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  geometry: { type: 'LineString', coordinates: [] },
                  properties: {},
                },
              });
              map.addLayer({
                id: ROUTE_CASING_LAYER,
                type: 'line',
                source: ROUTE_SOURCE,
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': '#ffffff', 'line-width': 7, 'line-opacity': 0.95 },
              });
              map.addLayer({
                id: ROUTE_LINE_LAYER,
                type: 'line',
                source: ROUTE_SOURCE,
                layout: { 'line-cap': 'round', 'line-join': 'round' },
                paint: { 'line-color': '#222222', 'line-width': 4, 'line-opacity': 0.95 },
              });
            }
            apply();
          } catch (e) {
            // Style not ready — retry shortly.
            setTimeout(() => !cancelled && safeAdd(), 200);
          }
        };
        if (map.isStyleLoaded?.()) safeAdd();
        else map.once('styledata', safeAdd);
        return;
      }

      if (!coords || coords.length < 2 || destination?.lng == null) {
        source.setData({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [] },
          properties: {},
        });
        map.easeTo({ center: [lng, lat], zoom, duration: 500 });
        return;
      }

      // Add the destination marker (pulse + core dot).
      const el = buildDestinationMarkerEl();
      destMarkerRef.current = new window.mapboxgl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat([destination.lng, destination.lat])
        .addTo(map);

      // Fit bounds to include both points with comfortable padding.
      const bounds = new window.mapboxgl.LngLatBounds();
      bounds.extend([lng, lat]);
      bounds.extend([destination.lng, destination.lat]);
      coords.forEach(c => bounds.extend(c));
      map.fitBounds(bounds, {
        padding: { top: 60, bottom: 80, left: 60, right: 60 },
        duration: 700,
        maxZoom: 16,
      });

      // Uber-style progressive draw.
      const start = performance.now();
      const tick = now => {
        if (cancelled || !mapRef.current) return;
        const elapsed = now - start;
        const t = Math.min(1, elapsed / ROUTE_ANIMATION_MS);
        const ease = easeOutCubic(t);
        const cutoff = Math.max(2, Math.ceil(coords.length * ease));
        const slice = coords.slice(0, cutoff);
        source.setData({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: slice },
          properties: {},
        });
        if (t < 1) {
          routeAnimationRef.current = requestAnimationFrame(tick);
        } else {
          source.setData({
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: coords },
            properties: {},
          });
        }
      };
      routeAnimationRef.current = requestAnimationFrame(tick);
    };

    // Poll briefly to wait out the async init useEffect (which sets mapRef
    // after awaiting loadMapboxGl). 50 attempts * 80 ms ≈ 4 s max wait. A map
    // created later (it waits for the viewport) bumps mapReadyTick, which
    // re-runs this effect.
    let attempts = 0;
    const startWhenReady = () => {
      if (cancelled) return;
      const map = mapRef.current;
      if (map) {
        apply();
      } else if (attempts++ < 50) {
        setTimeout(startWhenReady, 80);
      }
    };
    startWhenReady();

    return () => {
      cancelled = true;
      if (routeAnimationRef.current) cancelAnimationFrame(routeAnimationRef.current);
      if (destMarkerRef.current) {
        try {
          destMarkerRef.current.remove();
        } catch (e) {}
        destMarkerRef.current = null;
      }
    };
  }, [route, destination?.lng, destination?.lat, lng, lat, zoom, mapReadyTick]);

  // Update center if the listing point changes after first mount AND no route
  // is shown (route layout owns the camera otherwise).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || lng == null || lat == null) return;
    if (route?.line?.geometry?.coordinates?.length) return;
    map.setCenter([lng, lat]);
    if (markerRef.current) markerRef.current.setLngLat([lng, lat]);
  }, [lng, lat, route]);

  return (
    <div className={css.root}>
      <div ref={containerRef} className={css.mapContainer} aria-hidden="true" />
    </div>
  );
};

export default AirbnbStyleMap;
